import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureCosmosDBLinkedService } from 'app/model/linkedservice/azure-cosmos-db.linkedservice';
import { Constants } from 'app/shared/shared-constants';
import { akvToResource, akvToModel, parameterizableSecretBaseToModel, passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { connectionStringPartToResource, connectionStringPartToModel } from 'app/shared/utils/linkedservice-parameter.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext, ReferenceAdapter, Credential, cosmosSqlAuthMethod, CredentialTypeOptions } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureCosmosDBLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: AzureCosmosDBLinkedService) => {
                    if (ls && !ls.fullyByJsonEditor && ls.authenticationMethod === cosmosSqlAuthMethod.connectionString && !ls.enableAKVForConnectionString) {
                        // we only need handle connection string auth method because the other two authentication methods don't have connection string property
                        // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                        // won't cover fully by json for now
                        if (ls.accessKey instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                accountKey: 'secret',
                encryptedCredential: '*',
                database: '*',
                accountEndpoint: '*',
                tenant: '*',
                servicePrincipalId: '*',
                servicePrincipalCredentialType: '*',
                servicePrincipalCredential: 'secret',
                servicePrincipalKey: 'secret',
                credential: '*'
            },
            parameters: '*'
        }
    }
})
export class AzureCosmosLinkedServiceAdapter extends LinkedServiceAdapter<AzureCosmosDBLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureCosmosDBLinkedService): Promise<void> {
        let finalAuthType: string = model.authenticationMethod;
        if (model.credential) {
            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.ServicePrincipal) {
                finalAuthType = cosmosSqlAuthMethod.servicePrincipal;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = cosmosSqlAuthMethod.userAssigedManagedIdentity;
            } else if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = cosmosSqlAuthMethod.userAuth;
            }
        }
        model.authenticationMethod = finalAuthType;
    }

    protected modelConstructor(): new () => AzureCosmosDBLinkedService {
        return AzureCosmosDBLinkedService;
    }

    protected model(): IModel {
        return AzureCosmosDBLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureCosmosDBLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.CosmosDbLinkedService;
        properties.type = Metamodel.modelFor(AzureCosmosDBLinkedService).name;

        if (linkedService.authenticationMethod === cosmosSqlAuthMethod.connectionString) {
            if (!linkedService.enableAKVForConnectionString) {
                const builder = new DbConnectionStringBuilderService();
                builder.add('AccountEndpoint', connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.ACCOUNT_ENDPOINT));
                builder.add('Database', connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.DATABASE_NAME));

                if (!(linkedService.accessKey instanceof AzureKeyVault)) {
                    const accessKey = connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.ACCOUNT_KEY);
                    if (accessKey) {
                        builder.add('AccountKey', accessKey);
                    }
                }

                linkedService.additionalFields?.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });

                const connectionString = linkedService.accountEndpoint === Constants.maskedCredential &&
                    linkedService.accessKey === Constants.maskedCredential &&
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    linkedService.databaseName === Constants.maskedCredential ? {
                        type: 'SecureString',
                        value: Constants.maskedCredential
                    } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
                properties.typeProperties = {
                    connectionString,
                    accountKey: linkedService.accessKey instanceof AzureKeyVault ? akvToResource(linkedService.accessKey, this.context) : undefined
                };
            } else {
                properties.typeProperties = {
                    connectionString: akvToResource(linkedService.referedAKVForConnectionString, this.context)
                };
            }
        } else if (linkedService.authenticationMethod === cosmosSqlAuthMethod.servicePrincipal) {
            properties.typeProperties = {
                accountEndpoint: connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.ACCOUNT_ENDPOINT),
                database: connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.DATABASE_NAME),
                tenant: connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.TENANT),
                servicePrincipalId: connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.SERVICE_PRINCIPAL_ID),
                servicePrincipalCredentialType: linkedService.servicePrincipalCredentialType,
                servicePrincipalCredential: linkedService.servicePrincipalCredentialType === 'ServicePrincipalKey' ?
                    passwordOrAKVtoSecretBase(linkedService.servicePrincipalKey, this.context) :
                    akvToResource(linkedService.servicePrincipalCert, this.context)
            };
        } else if (linkedService.authenticationMethod === cosmosSqlAuthMethod.managedIdentity) {
            properties.typeProperties = {
                accountEndpoint: connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.ACCOUNT_ENDPOINT),
                database: connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.DATABASE_NAME)
            };
        } else if (linkedService.authenticationMethod === cosmosSqlAuthMethod.userAssigedManagedIdentity
            || linkedService.authenticationMethod === cosmosSqlAuthMethod.userAuth) {
            properties.typeProperties = {
                accountEndpoint: connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.ACCOUNT_ENDPOINT),
                database: connectionStringPartToResource(linkedService, AzureCosmosDBLinkedService.DATABASE_NAME),
                credential: linkedService.credential
                    ? (ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference)
                    : undefined
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureCosmosDBLinkedService): Promise<AzureCosmosDBLinkedService> {
        const properties = linkedServiceResource.properties as Model.CosmosDbLinkedService;
        linkedService.reset();

        if (!!properties.typeProperties.connectionString) {
            linkedService.authenticationMethod = cosmosSqlAuthMethod.connectionString;
            if (isString(properties.typeProperties.connectionString)) {
                const builder = new DbConnectionStringBuilderService();
                builder.parse(properties.typeProperties.connectionString as string);

                connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.ACCOUNT_ENDPOINT, builder.getValue('AccountEndpoint', false) || '');
                connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.ACCOUNT_KEY, builder.getValue('AccountKey', false) || '', true);
                connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.DATABASE_NAME, builder.getValue('Database', false) || '');
                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['AccountEndpoint', 'Database', 'AccountKey']);
                if (properties.typeProperties.accountKey) {
                    parameterizableSecretBaseToModel(linkedService, AzureCosmosDBLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
                }
            } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
                linkedService.accountEndpoint = Constants.maskedCredential;
                linkedService.accessKey = Constants.maskedCredential;
                linkedService.databaseName = Constants.maskedCredential;
                if (properties.typeProperties.accountKey) {
                    parameterizableSecretBaseToModel(linkedService, AzureCosmosDBLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
                }
            } else if (properties.typeProperties.connectionString
                && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.referedAKVForConnectionString = akvToModel(properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
                linkedService.enableAKVForConnectionString = true;
            } else if (stringIsNullOrEmpty(properties.typeProperties.connectionString)) {
                parameterizableSecretBaseToModel(linkedService, AzureCosmosDBLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
                linkedService.accountEndpoint = properties.typeProperties.accountEndpoint;
                linkedService.databaseName = properties.typeProperties.database;
            }
        } else if (!!properties.typeProperties.servicePrincipalId) {
            linkedService.authenticationMethod = cosmosSqlAuthMethod.servicePrincipal;
            connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.ACCOUNT_ENDPOINT, properties.typeProperties.accountEndpoint);
            connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.DATABASE_NAME, properties.typeProperties.database);
            connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.SERVICE_PRINCIPAL_ID, properties.typeProperties.servicePrincipalId);
            connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.TENANT, properties.typeProperties.tenant);
            linkedService.servicePrincipalCredentialType = properties.typeProperties.servicePrincipalCredentialType;
            if (linkedService.servicePrincipalCredentialType === 'ServicePrincipalKey') {
                linkedService.servicePrincipalKey = secretBaseToPasswordOrAKV(properties.typeProperties.servicePrincipalCredential, true, this.context);
            } else {
                linkedService.servicePrincipalCert = akvToModel(properties.typeProperties.servicePrincipalCredential, this.context);
            }
        } else if (properties.typeProperties.credential) {
            // both uami & userauth have credential, let postToModel() decide
            connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.ACCOUNT_ENDPOINT, properties.typeProperties.accountEndpoint);
            connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.DATABASE_NAME, properties.typeProperties.database);
            linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), properties.typeProperties.credential, this.context);
        } else {
            linkedService.authenticationMethod = cosmosSqlAuthMethod.managedIdentity;
            connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.ACCOUNT_ENDPOINT, properties.typeProperties.accountEndpoint);
            connectionStringPartToModel(linkedService, AzureCosmosDBLinkedService.DATABASE_NAME, properties.typeProperties.database);
        }

        return linkedService;
    }
}
